import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { CheckCircle, Loader2, AlertCircle, Calendar, Clock, Car, MapPin, Phone, Mail, Printer, Download, Home } from 'lucide-react';
import QRCode from 'react-qr-code';
import './ConfirmationPage.css';

function ConfirmationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [bookingStatus, setBookingStatus] = useState(null);
  const [error, setError] = useState(null);
  
  // Get booking data from location state
  const bookingData = location.state;
  const bookingId = searchParams.get('booking_id') || bookingData?.booking_id;
  const fromPayment = searchParams.get('from') === 'payment';
  const type = searchParams.get('type') || bookingData?.type;
  const isBooking = type === 'booking';

  useEffect(() => {
    // If coming back from payment, check booking status with CRM
    if (fromPayment && bookingId) {
      checkPaymentStatus();
    }
  }, [fromPayment, bookingId]);

  const checkPaymentStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookings/${bookingId}/status`
      );
      
      if (response.ok) {
        const data = await response.json();
        setBookingStatus(data);
        
        // Check if payment succeeded
        if (data.payment_status !== 'paid') {
          setError('Greiðsla hefur ekki verið staðfest enn. Vinsamlegast reyndu aftur eða hafðu samband.');
        }
      } else {
        setError('Ekki tókst að sækja bókunarstöðu');
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      setError('Villa kom upp við að athuga greiðslustöðu');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('is-IS', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Show loading while checking payment status
  if (loading) {
    return (
      <div className="min-h-screen py-12 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#0A4D8C] mx-auto mb-4" />
          <p className="text-lg text-[#8B9BA8]">Athuga greiðslustöðu...</p>
        </div>
      </div>
    );
  }

  // Show error if payment verification failed
  if (error) {
    return (
      <div className="min-h-screen py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-[#EF4444] shadow-xl">
            <CardContent className="pt-12 pb-8 text-center">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-[#EF4444] rounded-full flex items-center justify-center">
                  <AlertCircle className="w-12 h-12 text-white" />
                </div>
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-[#2C3E50] mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Villa kom upp
              </h1>

              <p className="text-lg text-[#8B9BA8] mb-8">
                {error}
              </p>

              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/booking')}
                  className="w-full bg-[#0A4D8C] hover:bg-[#083A6B] text-white py-6 text-lg rounded-lg"
                >
                  Reyna aftur
                </Button>
                <Button 
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="w-full border-2 border-[#0A4D8C] text-[#0A4D8C] py-6 text-lg rounded-lg"
                >
                  Fara á forsíðu
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 bg-gradient-to-b from-white to-[#F8FAFB]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Header */}
        <div className="text-center mb-8 print-hide">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-[#10B981] rounded-full mb-6 animate-bounce">
            <CheckCircle className="w-14 h-14 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#2C3E50] mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            {isBooking ? 'Bókun Staðfest!' : 'Pöntun Staðfest!'}
          </h1>
          <p className="text-xl text-[#8B9BA8]">
            Þakka þér fyrir að velja Alvörubón
          </p>
        </div>

        {/* Booking Details Card */}
        <Card className="border-[#E8ECEF] shadow-2xl mb-6">
          <CardHeader className="bg-gradient-to-r from-[#E8F4F8] to-[#F8FAFB] border-b border-[#E8ECEF]">
            <CardTitle className="text-2xl text-[#2C3E50]">
              Bókunarupplýsingar
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8 space-y-6">
            {/* Booking ID */}
            {bookingId && (
              <div className="flex items-center justify-between p-4 bg-[#F8FAFB] rounded-lg border border-[#E8ECEF]">
                <div>
                  <p className="text-sm text-[#8B9BA8] mb-1">Bókunarnúmer</p>
                  <p className="text-2xl font-mono font-bold text-[#0A4D8C]">
                    #{bookingId.slice(0, 8).toUpperCase()}
                  </p>
                </div>
                <div className="w-24 h-24 bg-white p-2 rounded border border-[#E8ECEF]" data-testid="booking-qr-code">
                  <QRCode 
                    value={bookingId}
                    size={80}
                    level="M"
                    style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                  />
                </div>
              </div>
            )}

            <Separator />

            {/* Date & Time */}
            {bookingData?.date && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InfoItem 
                  icon={<Calendar className="w-5 h-5 text-[#0A4D8C]" />}
                  label="Dagsetning"
                  value={formatDate(bookingData.date) || bookingData.date}
                />
                <InfoItem 
                  icon={<Clock className="w-5 h-5 text-[#0A4D8C]" />}
                  label="Tími"
                  value={bookingData.time || 'Óþekkt'}
                />
              </div>
            )}

            {/* Vehicle Info */}
            {bookingData?.plate_number && (
              <div>
                <InfoItem 
                  icon={<Car className="w-5 h-5 text-[#0A4D8C]" />}
                  label="Bíll"
                  value={`${bookingData.plate_number} ${bookingData.vehicle || ''}`}
                />
              </div>
            )}

            <Separator />

            {/* Price */}
            {(bookingStatus?.total || bookingData?.total) && (
              <div className="bg-gradient-to-r from-[#E8F4F8] to-white p-6 rounded-lg border-2 border-[#4A9FD8]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#8B9BA8] mb-1">Heildarverð</p>
                    <p className="text-3xl font-bold text-[#0A4D8C]">
                      {(bookingStatus?.total || bookingData?.total).toLocaleString()} kr.
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-[#8B9BA8] mb-1">Greiðsla</p>
                    {bookingData?.payment_method === 'on_site' || !bookingData?.payment_method ? (
                      <p className="text-lg font-semibold text-[#F59E0B]">Á staðnum</p>
                    ) : (
                      <p className="text-lg font-semibold text-[#10B981]">✓ Greitt</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Next Steps Card */}
        <Card className="border-[#E8ECEF] shadow-xl mb-6 print-hide">
          <CardHeader className="bg-[#F8FAFB] border-b border-[#E8ECEF]">
            <CardTitle className="text-xl text-[#2C3E50]">
              Næstu skref
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <StepItem 
              number="1"
              title="Staðfesting send"
              description="Staðfestingarpóstur hefur verið sendur á netfangið þitt með öllum upplýsingum"
            />
            <StepItem 
              number="2"
              title="Mættu á réttum tíma"
              description="Vinsamlegast mættu 5-10 mínútum fyrir bókaðan tíma. Aðsetning: Viðarhöfða 2a, 110 Reykjavík"
            />
            <StepItem 
              number="3"
              title="Njóttu þjónustunnar"
              description="Við sjáum um bílinn þinn með fagmennsku og umhyggju"
            />
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 no-print">
          <Button 
            onClick={handlePrint}
            variant="outline"
            className="border-2 border-[#0A4D8C] text-[#0A4D8C] hover:bg-[#E8F4F8] py-6 text-lg"
          >
            <Printer className="mr-2 h-5 w-5" />
            Prenta staðfestingu
          </Button>
          <Button 
            onClick={() => navigate('/')}
            className="bg-[#0A4D8C] hover:bg-[#083A6B] text-white py-6 text-lg"
          >
            <Home className="mr-2 h-5 w-5" />
            Fara á forsíðu
          </Button>
        </div>

        {/* Contact Card */}
        <Card className="border-[#E8ECEF] shadow-xl bg-gradient-to-r from-[#E8F4F8] to-white print-hide">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-[#2C3E50] mb-4">
              Spurningar eða breytingar?
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-[#8B9BA8]">
                <Phone className="w-4 h-4 text-[#0A4D8C]" />
                <a href="tel:5880705" className="hover:text-[#0A4D8C] hover:underline">
                  588-0705
                </a>
              </div>
              <div className="flex items-center gap-2 text-[#8B9BA8]">
                <Mail className="w-4 h-4 text-[#0A4D8C]" />
                <a href="mailto:alvorubon@alvorubon.is" className="hover:text-[#0A4D8C] hover:underline">
                  alvorubon@alvorubon.is
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function InfoItem({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="p-2 bg-[#E8F4F8] rounded-lg">
        {icon}
      </div>
      <div>
        <p className="text-sm text-[#8B9BA8] mb-1">{label}</p>
        <p className="text-lg font-semibold text-[#2C3E50]">{value}</p>
      </div>
    </div>
  );
}

function StepItem({ number, title, description }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-10 h-10 bg-[#0A4D8C] text-white rounded-full flex items-center justify-center font-bold">
        {number}
      </div>
      <div>
        <h4 className="font-semibold text-[#2C3E50] mb-1">{title}</h4>
        <p className="text-sm text-[#8B9BA8]">{description}</p>
      </div>
    </div>
  );
}

export default ConfirmationPage;
